/**
 * QuickbladeSkillEffect — Blades tree evolution, replaces Stab.
 *
 * Instant directional thrust with coherence-light trail. Inherits Hide
 * break from Stab. Key differences from Stab:
 * - 40% more base damage (1.4× multiplier)
 * - Flat 200ms recovery (ignores recoveryMs modifiers from Clean Entry)
 * - 15% armor bypass on every hit
 *
 * Upgrade inheritance: queries resolver with 'stab' skillId so Clean
 * Entry's damage multiplier stacks (1.4 × 1.2 = 1.68). Recovery modifier
 * is explicitly NOT applied — Quickblade has its own flat recovery.
 *
 * Armor bypass is exposed as a resolved value for CombatHitResolver to
 * apply: `target.effectiveArmor * (1 - armorBypass)`.
 *
 * Implements SkillEffect directly with own cooldown management.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see blades-spec.md § Quickblade
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface QuickbladeDeps {
  readonly resolver: {
    getMultiplicativeEffect(skillId: string, key: string): number;
  };
  readonly combat: {
    startAttack(attackType: string): void;
  };
  readonly concealment: {
    isConcealed(): boolean;
    breakHide(): void;
  };
  readonly rollSkillSuccess: (skillId: string) => boolean;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface QuickbladeConfig {
  readonly recoveryMs: number;
  readonly baseDamageMultiplier: number;
  readonly armorBypass: number;
}

const DEFAULT_CONFIG: QuickbladeConfig = {
  recoveryMs: 200,                  // flat 200ms — not modified by resolver
  baseDamageMultiplier: 1.4,        // 40% more than base Stab
  armorBypass: 0.15,                // 15% armor bypass
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class QuickbladeSkillEffect implements SkillEffect {
  readonly skillId = 'stab_evolved';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: QuickbladeDeps;
  private readonly config: QuickbladeConfig;

  private _cooldownRemaining = 0;
  private _attacksFired = 0;
  private _fizzles = 0;
  private _hidesBroken = 0;
  private _resolvedDamageMultiplier = 1.0;
  private _wasConcealed = false;

  constructor(deps: QuickbladeDeps, config: Partial<QuickbladeConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get attacksFired(): number { return this._attacksFired; }
  get fizzles(): number { return this._fizzles; }
  get hidesBroken(): number { return this._hidesBroken; }
  get resolvedDamageMultiplier(): number { return this._resolvedDamageMultiplier; }
  get recoveryMs(): number { return this.config.recoveryMs; }
  get armorBypass(): number { return this.config.armorBypass; }
  get wasConcealed(): boolean { return this._wasConcealed; }

  get cooldown(): number { return this._cooldownRemaining; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — cooldown consumed even on fizzle
    if (!this.deps.rollSkillSuccess('stab_evolved')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.recoveryMs;
      return false;
    }

    // Resolve damage multiplier (base 1.4 × inherited Clean Entry)
    // Queries 'stab' for upgrade inheritance via replacesId aliasing
    this._resolvedDamageMultiplier = this.config.baseDamageMultiplier *
      this.deps.resolver.getMultiplicativeEffect('stab', 'damage');

    // Check and break Hide
    this._wasConcealed = this.deps.concealment.isConcealed();
    if (this._wasConcealed) {
      this.deps.concealment.breakHide();
      this._hidesBroken++;
    }

    // Fire the attack
    this.deps.combat.startAttack('quickblade');
    this._attacksFired++;

    // Flat recovery — not modified by resolver
    this._cooldownRemaining = this.config.recoveryMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
